// src/modules/leads/route-planner/RoutePlannerPage.js
import React, { useState, useCallback } from 'react';
import DashboardLayout from '../../../layouts/DashboardLayout';
import useGoogleMapsLoader from './useGoogleMapsLoader';
import useRoutePlanner from './useRoutePlanner';
import PermissionOnboarding from './PermissionOnboarding';
import RoutePlannerStepIndicator from './RoutePlannerStepIndicator';
import RoutePlannerMap from './RoutePlannerMap';
import RoutePlannerSidebar from './RoutePlannerSidebar';
import RoutePlannerMobilePanel from './RoutePlannerMobilePanel';
import { PageWrapper, PlannerContainer, EmptyState, PacContainerStyles } from './routePlannerStyles';

const RoutePlannerPage = () => {
  const { isLoaded: isGoogleLoaded, error: googleError } = useGoogleMapsLoader();
  const [onboardingDone, setOnboardingDone] = useState(false);

  const planner = useRoutePlanner(isGoogleLoaded);
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);

  const handleLeadClick = useCallback((lead) => {
    setHighlightedLeadId(lead.id);
  }, []);

  const handleOnboardingComplete = useCallback(() => {
    setOnboardingDone(true);
  }, []);

  // ── Show onboarding before everything else ────────────────────────────────
  if (!onboardingDone) {
    return (
      <DashboardLayout>
        <PermissionOnboarding
          isGoogleLoaded={isGoogleLoaded}
          googleError={googleError}
          onComplete={handleOnboardingComplete}
        />
      </DashboardLayout>
    );
  }

  // ── Google Maps error after onboarding ────────────────────────────────────
  if (googleError) {
    return (
      <DashboardLayout>
        <EmptyState>
          <div style={{ fontSize: '1.2rem', marginBottom: '8px' }}>Failed to load Google Maps</div>
          <div style={{ color: '#e74c3c' }}>{googleError}</div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '16px', padding: '10px 24px', background: '#F69620',
              color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </EmptyState>
      </DashboardLayout>
    );
  }

  // ── Main planner (Google Maps is guaranteed loaded at this point) ──────────
  return (
    <DashboardLayout>
      <PacContainerStyles />
      <PageWrapper>
        <RoutePlannerStepIndicator currentStep={planner.currentStep} />

        <PlannerContainer>
          {/* Desktop sidebar */}
          <RoutePlannerSidebar planner={planner} onLeadClick={handleLeadClick} />

          {/* Map */}
          <RoutePlannerMap
            isGoogleLoaded={isGoogleLoaded}
            departure={planner.departure}
            destination={planner.effectiveDestination}
            mappableLeads={planner.mappableLeads}
            selectedLeadIds={planner.selectedLeadIds}
            routeResponse={planner.routeResponse}
            baseRouteResponse={planner.baseRouteResponse}
            routeStops={planner.routeStops}
            alongRouteLeads={planner.alongRouteLeads}
            showAlongRoute={planner.showAlongRoute}
            isOptimizing={planner.isOptimizing}
            onToggleLead={planner.toggleLeadInRoute}
            onAddAlongRouteLead={planner.addAlongRouteLeadToRoute}
            highlightedLeadId={highlightedLeadId}
          />
        </PlannerContainer>

        {/* Mobile bottom sheet */}
        <RoutePlannerMobilePanel planner={planner} onLeadClick={handleLeadClick} />
      </PageWrapper>
    </DashboardLayout>
  );
};

export default RoutePlannerPage;
